import {
  Avatar,
  IconButton,
  Stack,
  TextBody,
  Tooltip,
} from '@pypestream/design-system';
import type { FC } from 'react';

import type { ProjectUser } from '../../xstate/prostudio.api-service.types';
import type { SolutionUserPresenceItem } from '../../xstate/prostudio.xstate.types';
import { VIEWER_BORDER_COLOR_COUNT } from '../../utils/get-viewer-color-index';

import './active-solution-users.scss';

export type ActiveSolutionUsersAvatarsVariant = 'inline' | 'inline-compact';

export type ActiveSolutionUsersAvatarsProps = {
  presences: SolutionUserPresenceItem[];
  projectUsers?: ProjectUser[];
  maxVisible?: number;
  variant: ActiveSolutionUsersAvatarsVariant;
  /** For variant="inline", the id of the overflow button so Popover can use trigger */
  popoverTriggerId?: string;
  /** Called when clicking a collaborator's avatar to jump to their focused cell */
  onAvatarClick?: (presence: SolutionUserPresenceItem) => void;
};

function getDisplayUserForPresence(
  presence: SolutionUserPresenceItem,
  projectUsers?: ProjectUser[]
): { firstName?: string; lastName?: string; picture?: string | null } | null {
  if (presence.user) {
    return presence.user;
  }
  const found = projectUsers?.find((pu) =>
    pu?.id ? String(pu.id) === String(presence.userId) : false
  );
  return found
    ? {
        firstName: found.firstName,
        lastName: found.lastName,
        picture: found.picture ?? undefined,
      }
    : null;
}

function getDisplayName(
  presence: SolutionUserPresenceItem,
  projectUsers?: ProjectUser[],
  fallback = 'Viewing'
): string {
  const displayUser = getDisplayUserForPresence(presence, projectUsers);
  const name = [displayUser?.firstName, displayUser?.lastName]
    .filter(Boolean)
    .join(' ');
  return name || (displayUser ? fallback : 'Someone else');
}

export const ActiveSolutionUsersAvatars: FC<
  ActiveSolutionUsersAvatarsProps
> = ({
  presences,
  projectUsers,
  maxVisible = 3,
  variant,
  popoverTriggerId = 'viewers',
  onAvatarClick,
}) => {
  const visible = presences.slice(0, maxVisible);
  const extraCount = presences.length - maxVisible;

  return (
    <div className="c-viewer-avatar-rings">
      {visible.map((presence, index) => {
        const displayName = getDisplayName(presence, projectUsers);
        const displayUser = getDisplayUserForPresence(presence, projectUsers);
        const colorIndex = index % VIEWER_BORDER_COLOR_COUNT;
        const hasCell = !!presence.pageParams?.focusedCell;
        return (
          <Tooltip key={presence.id} content={displayName}>
            <span
              className={`c-viewer-avatar-rings__item c-viewer-avatar-rings__item--color-${colorIndex}`}
              onClick={
                onAvatarClick && hasCell
                  ? () => onAvatarClick(presence)
                  : undefined
              }
              style={
                onAvatarClick && hasCell ? { cursor: 'pointer' } : undefined
              }
            >
              <Avatar
                label={displayName}
                src={displayUser?.picture ?? undefined}
              />
            </span>
          </Tooltip>
        );
      })}
      {variant === 'inline' ? (
        <IconButton
          className="c-viewer-avatar-rings__item c-viewer-avatar-rings__item--view-all"
          id={popoverTriggerId}
          rounded
        >
          <Avatar>{extraCount > 0 ? `+${extraCount}` : '⋯'}</Avatar>
        </IconButton>
      ) : extraCount > 0 ? (
        <span className="c-viewer-avatar-rings__item c-viewer-avatar-rings__item--view-all">
          <Avatar>+{extraCount}</Avatar>
        </span>
      ) : null}
    </div>
  );
};

/** List item for use in popover (e.g. "Viewing this solution" list) */
export const ActiveSolutionUsersListContent: FC<{
  presences: SolutionUserPresenceItem[];
  projectUsers?: ProjectUser[];
  /** Called when clicking a collaborator's row to jump to their focused cell */
  onItemClick?: (presence: SolutionUserPresenceItem) => void;
}> = ({ presences, projectUsers, onItemClick }) => (
  <>
    {presences.map((presence, index) => {
      const displayName = getDisplayName(presence, projectUsers, 'Unknown');
      const displayUser = getDisplayUserForPresence(presence, projectUsers);
      const colorIndex = index % VIEWER_BORDER_COLOR_COUNT;
      const hasCell = !!presence.pageParams?.focusedCell;
      const clickable = onItemClick && hasCell;
      return (
        <div
          key={presence.id}
          onClick={clickable ? () => onItemClick(presence) : undefined}
          style={clickable ? { cursor: 'pointer' } : undefined}
        >
          <Stack alignItems="center" gutter="small">
            <span
              className={`c-viewer-avatar-rings__item c-viewer-avatar-rings__item--color-${colorIndex}`}
            >
              <Avatar
                label={displayName}
                src={displayUser?.picture ?? undefined}
              />
            </span>
            <TextBody>{displayName}</TextBody>
          </Stack>
        </div>
      );
    })}
  </>
);
